// ============================================================================
// SCOPE ITEM ID HELPERS
// ----------------------------------------------------------------------------
// Stable, collision-free identifiers for scope items. Before this helper was
// introduced, items were distinguished by `code` (positional, regenerated on
// every reorder/delete) or by array index (shifts on add/remove). That was
// fine for render keys but broke:
//   • Per-item cost-recompute locks (two items with code="1" shared a lock)
//   • Async recompute staleness detection (index drift after reorder)
//   • Undo/redo item tracking (snapshots restore by value, not identity)
//
// Every scope item is now minted with a UUID at creation time. Legacy projects
// loaded from Dexie get migrated on first load via `ensureItemIds(scope)`.
//
// ID generation strategy (highest-quality first):
//   1. crypto.randomUUID()   - native RFC 4122 v4, available on all modern
//                              browsers (Chromium ≥ 92, Firefox ≥ 95, Safari ≥ 15.4)
//   2. crypto.getRandomValues + hand-rolled v4 - available since IE 11,
//                              provides the same RFC 4122 v4 quality (122 bits
//                              of entropy) using a documented bit-layout.
//   3. Last-resort pseudo-random - combines Date.now() microsecond resolution,
//                              a monotonic process-wide counter that survives
//                              same-ms creation, and Math.random(). Only used
//                              if neither crypto path is available (e.g. an
//                              ancient WebView or a unit test stub).
//
// All three paths return a "sid-" prefixed string so they're easy to spot
// in logs and Dexie inspectors, and so the prefix acts as a schema marker
// in case we ever need to migrate the format again.
// ============================================================================

// Process-wide monotonic counter. Even in the worst-case last-resort path,
// two ids created in the same millisecond still differ because the counter
// increments on every call. Seeded with a random offset so two tabs don't
// start at the same value.
let _seq = Math.floor(Math.random() * 0xffff);

/**
 * A scope item carrying a stable `id`. Permissive (index signature) because
 * scope items carry many domain fields this helper doesn't care about; it only
 * reads/writes `id`.
 */
export interface ScopeItemWithId {
    id?: string;
    [key: string]: unknown;
}

/**
 * RFC 4122 v4 UUID via crypto.getRandomValues. The bit layout:
 *   - Byte 6: top nibble = 4 (version)
 *   - Byte 8: top two bits = 10 (variant)
 * All other bits are random.
 *
 * @returns {string} An RFC 4122 v4 UUID string.
 */
function uuidv4FromGetRandomValues(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // Per RFC 4122 §4.4: set bits for version and clock_seq_hi_and_reserved.
    // Every index 0..15 is provably present: `bytes` is a fixed-length 16-byte
    // Uint8Array that getRandomValues fully populates. The `!` assertions keep
    // the runtime byte-identical to the .js original while satisfying
    // noUncheckedIndexedAccess (typed-array indexing widens to `number | undefined`).
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;   // version 4
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;   // variant (10xx)
    const hex: string[] = [];
    for (let i = 0; i < 16; i++) {
        hex.push((bytes[i]! + 0x100).toString(16).slice(1));
    }
    // 8-4-4-4-12 formatting
    return (
        hex[0]! + hex[1]! + hex[2]! + hex[3]! + '-' +
        hex[4]! + hex[5]! + '-' +
        hex[6]! + hex[7]! + '-' +
        hex[8]! + hex[9]! + '-' +
        hex[10]! + hex[11]! + hex[12]! + hex[13]! + hex[14]! + hex[15]!
    );
}

/**
 * Generate a stable id for a scope item. Prefers native crypto APIs; falls
 * back to a counter-augmented pseudo-random path only when neither is
 * available (extremely rare in production browsers).
 *
 * Collision analysis for the fallback path:
 *   - Date.now() gives ms-resolution timestamps
 *   - _seq monotonically increments, wrapping at 2^32
 *   - Math.random() contributes ~32 bits of entropy
 * Two ids can only collide if the caller creates 2^32 ids in the same
 * millisecond AND Math.random() returns identical values. That is not a
 * realistic scenario for a scope builder used by human operators.
 *
 * @returns A string guaranteed unique within the session, and - on
 *          the fast paths - globally unique per RFC 4122.
 */
export function genItemId(): string {
    _seq = (_seq + 1) >>> 0; // wrap at 2^32

    try {
        if (typeof crypto !== 'undefined') {
            if (typeof crypto.randomUUID === 'function') {
                return 'sid-' + crypto.randomUUID();
            }
            if (typeof crypto.getRandomValues === 'function') {
                return 'sid-' + uuidv4FromGetRandomValues();
            }
        }
    } catch {
        // fall through to last-resort path
    }

    // Last-resort path - counter + timestamp + Math.random hex.
    // Deliberately NOT claiming to be RFC 4122 (no version/variant bits set)
    // so a future consumer can distinguish these by absence of the dashes.
    const ts = Date.now().toString(36);
    const ctr = _seq.toString(36).padStart(7, '0');
    const r1 = Math.random().toString(36).slice(2, 10);
    const r2 = Math.random().toString(36).slice(2, 10);
    return `sid-fallback-${ts}-${ctr}-${r1}${r2}`;
}

/**
 * Return a new scope array where every item is guaranteed to have an `id`.
 * Existing ids are preserved (idempotent) - even an item with `id: ''`
 * (empty string) is treated as missing and gets a fresh id, which prevents
 * a foot-gun flagged by the audit team. Called on every project load so
 * legacy data - which never had ids - gets migrated transparently.
 *
 * Also filters out any null/undefined/non-object entries that may have
 * leaked in through upstream splice-and-filter bugs. Downstream consumers
 * like the margin-sync effect and undo snapshots assume every element is
 * a real item, so preserving nulls would crash `item.id` access later.
 *
 * @param scope
 * @returns A new array (not the same reference) with stable ids
 */
export function ensureItemIds(scope: unknown): ScopeItemWithId[] {
    if (!Array.isArray(scope)) return [];
    let mutated = false;
    // First pass: filter out invalid entries (null, undefined, primitives).
    // Only fire the mutation flag if we actually had to drop something.
    const filtered = scope.filter(it => {
        const ok = it != null && typeof it === 'object';
        if (!ok) mutated = true;
        return ok;
    }) as ScopeItemWithId[];
    // Second pass: mint ids for any item missing a non-empty string id.
    const next = filtered.map(it => {
        if (typeof it.id === 'string' && it.id.length > 0) return it;
        mutated = true;
        return { ...it, id: genItemId() };
    });
    // Return the same reference when nothing changed so React skips re-renders.
    return mutated ? next : (scope as ScopeItemWithId[]);
}
